import { BuildingOffice2Icon, ChatBubbleLeftRightIcon, LightBulbIcon } from "@heroicons/react/24/outline";
import { ContactForm } from "../components/forms/ContactForm";
import { PageIntro } from "../components/ui/PageIntro";
import { useDocumentMeta } from "../lib/meta";

const contactHighlights = [
  {
    title: "General questions and introductions",
    body: "Use the form for general inquiries, donor questions, partnership outreach, and first-contact introductions to the organization.",
    Icon: ChatBubbleLeftRightIcon,
  },
  {
    title: "Project support inquiries",
    body: "If you are exploring fiscal sponsorship or mission-aligned project support, share the educational purpose of the idea and what kind of support you are seeking.",
    Icon: LightBulbIcon,
  },
  {
    title: "Secure message handling",
    body: "Submissions are validated server-side, stored in the database, and routed to internal Microsoft 365 recipients through Microsoft Graph.",
    Icon: BuildingOffice2Icon,
  },
] as const;

export const ContactPage = () => {
  useDocumentMeta({
    title: "Contact",
    description:
      "Contact The Education Project about partnerships, project support, donor questions, or general inquiries through a secure message flow.",
  });

  return (
    <div className="flex flex-col bg-white">
      <PageIntro
        eyebrow="Contact"
        title="Start a conversation with The Education Project."
        summary="Reach out about learning initiatives, partnerships, donor support, project ideas, or general questions. Messages are stored securely and routed through a protected internal workflow."
      />

      <div className="bg-[#0E1C2C] w-full -mt-12 -mb-24 relative z-10 border-t-4 border-t-[#CEDC00]">
        <section className="mx-auto max-w-7xl px-6 lg:px-8 py-16 w-full">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <div className="space-y-8 lg:pr-8">
              <div>
                <p className="text-xs uppercase font-bold tracking-widest text-[#CEDC00] mb-2">Communication channels</p>
                <h2 className="text-3xl font-display font-bold text-white mb-4">How we can help</h2>
                <p className="text-base text-slate-300 leading-relaxed">
                  Whether you are a donor, educator, partner, or community member, we have secure channels to review and route your ideas or inquiries.
                </p>
              </div>
 
              <div className="divide-y divide-white/10 border-t border-white/10">
                {contactHighlights.map(({ title, body, Icon }) => (
                  <article
                    key={title}
                    className="py-6 flex items-start gap-4"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/10 text-[#CEDC00]">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-display font-bold text-white">{title}</h3>
                      <p className="mt-1 text-sm text-slate-400 leading-relaxed">{body}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
 
            <div className="lg:pl-8 w-full">
              <h3 className="text-2xl font-display font-bold text-white mb-6">Send a Message</h3>
              <ContactForm />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
